<template>
  <LegalShell
    active="about"
    eyebrow="About"
    title="About Muzilly"
    subtitle="Listen · Belong · Get paid. A home for artists, rising artists and the fans who actually care."
  >
    <div class="lg-callout" style="margin-top:0">
      <div class="lg-callout-title">Our mission</div>
      <p class="lg-p">Music has always been free. Belonging never was. Muzilly exists so artists are paid directly by the fans who love them, not by advertisers, and so fans can be part of something instead of just feeding an algorithm. No ads. No data sold. No middlemen deciding who matters.</p>
    </div>

    <section class="lg-section">
      <h2 class="lg-h2">What we stand for</h2>
      <div class="lg-items">
        <div v-for="p in pillars" :key="p.t" class="lg-item"><span class="lg-dot" :class="p.c"></span><div><div class="lg-item-title">{{ p.t }}</div><div class="lg-item-text">{{ p.d }}</div></div></div>
      </div>
    </section>

    <section class="lg-section">
      <h2 class="lg-h2">Why we exist</h2>
      <p class="lg-p">Most music platforms are built around a giant catalogue funded by advertising and data. A tiny fraction of artists earn anything, and fans are the product. Muzilly starts from a different premise: listening is free, and a simple $5 monthly membership funds the platform and the artists directly. You're the customer, not the product.</p>
      <p class="lg-p">Anyone can sign up and start right away. There's no invitation and no waiting list.</p>
    </section>

    <section class="lg-section">
      <h2 class="lg-h2">How the money works</h2>
      <p class="lg-p">We'd rather you see the numbers than take our word for it.</p>
      <div class="lg-cards">
        <div class="lg-card"><div class="lg-card-title">50% · Artists</div><div class="lg-card-text">Of net membership revenue goes to Artist, Producer and Band accounts, shared by listening time.</div></div>
        <div class="lg-card"><div class="lg-card-title">10% · Rising artists</div><div class="lg-card-text">A separate pool for emerging artists, always shown on its own, so new talent isn't drowned out.</div></div>
        <div class="lg-card"><div class="lg-card-title">10% · Podcasts</div><div class="lg-card-text">A pool for podcasters, shared by listening time. It is always shown on its own.</div></div>
        <div class="lg-card"><div class="lg-card-title">30% · Muzilly</div><div class="lg-card-text">Pays for running the platform. That's the whole business model.</div></div>
      </div>
      <p class="lg-p" style="margin-top:16px">Every member gets <strong>one vote a month</strong>, split between the artists they follow by how long they listened. A thousand members who love you beat a million drive-by plays. Gifts to live-room hosts pay creators 70%. The full rules are in our <router-link class="lg-a" to="/terms">Terms</router-link>.</p>
    </section>

    <section class="lg-section">
      <h2 class="lg-h2">What you can do</h2>
      <div class="lg-cards">
        <div v-for="f in features" :key="f.t" class="lg-card"><div class="lg-card-title">{{ f.t }}</div><div class="lg-card-text">{{ f.d }}</div></div>
      </div>
      <p class="lg-p" style="margin-top:16px">Muzilly is on the web, iPhone and Android, with the same account everywhere.</p>
    </section>

    <section class="lg-section">
      <h2 class="lg-h2">Legal &amp; help</h2>
      <div class="lg-items">
        <router-link class="lg-item about-link" to="/faq"><div class="lg-item-title">FAQ</div><span class="lg-a">→</span></router-link>
        <router-link class="lg-item about-link" to="/privacy"><div class="lg-item-title">Privacy Policy</div><span class="lg-a">→</span></router-link>
        <router-link class="lg-item about-link" to="/terms"><div class="lg-item-title">Terms of Service</div><span class="lg-a">→</span></router-link>
        <router-link class="lg-item about-link" to="/delete-account"><div class="lg-item-title">Delete your account</div><span class="lg-a">→</span></router-link>
      </div>
    </section>

    <div class="lg-cta">
      <h3 class="lg-cta-title">Get in touch</h3>
      <p class="lg-cta-text">Questions, feedback, or something not working? We'd love to hear from you.</p>
      <a class="lg-btn" href="mailto:support@muzilly.com">Contact us</a>
    </div>
  </LegalShell>
</template>

<script>
import LegalShell from "@/components/LegalShell.vue";

export default {
  name: "AboutView",
  components: { LegalShell },
  data() {
    return {
      pillars: [
        { c: "", t: "Paid by the fans who listen", d: "Your share of the monthly pool comes from the members who follow and listen to you. What you earn grows with your audience." },
        { c: "teal", t: "The velvet rope is real", d: "Listen free. To comment, interact and belong, that's what membership is for." },
        { c: "", t: "Zero ads. Ever.", d: "We don't sell your attention or your data. Membership is how this works." },
        { c: "teal", t: "Open to everyone", d: "Fans, artists, producers, bands and rising artists. Sign up and you're in right away." },
      ],
      features: [
        { t: "Drops & albums", d: "Publish tracks, demos and albums with cover art, public or members-only." },
        { t: "Podcasts & music videos", d: "Anyone can start a podcast. Artists post music videos from Drop. Free for everyone or members only, and it remembers where you stopped on any device." },
        { t: "Live rooms", d: "Real-time voice rooms with chat, gifts, moderators and slow mode. Rooms aren't recorded." },
        { t: "Collab board & projects", d: "Post calls, apply, work in a shared project with files, a locked split sheet and release credit." },
        { t: "Co-signs & Discovery Score", d: "Established artists back rising talent publicly, and rising artists build a score to be found." },
        { t: "Messages", d: "Direct conversations with real notifications. Artists choose who can reach them." },
        { t: "Playlists, likes & comments", d: "Save what you love, tell artists what it meant to you." },
        { t: "Coins & gifts", d: "Send gifts to live hosts; creators keep 70%." },
        { t: "Tribzz", d: "Connect Muzilly to Tribzz, a social app for photos, videos, shorts and stories. Artists can let their songs play in posts there, and listening still pays through the same monthly pool." },
        { t: "Earnings & analytics", d: "Creators see what they have earned and how their music is doing, and cash out via Stripe." },
      ],
    };
  },
};
</script>

<style scoped>
.about-link{display:flex;justify-content:space-between;align-items:center;text-decoration:none;cursor:pointer}
.about-link:hover{border-color:rgba(212,140,28,0.4)}
</style>